import React, { useCallback, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DATA_GRID_DEFAULT_PAGE_SIZE } from '../../../../components/DataGridNumberedPagination';
import { DataGrid, GridActionsCellItem, useGridApiRef, type GridColDef } from '@mui/x-data-grid';
import type { DynamicEntityRow } from '../../../../api/strapiDynamicEntity';
import { useAuth } from '../../../../auth/AuthContext';
import {
  useCreateQuotationLogMutation,
  useDeleteQuotationLogMutation,
  useQuotationLogsQuery,
  useUpdateQuotationLogMutation,
} from '../../../../query/hooks';
import { ChangeLogCell } from './ChangeLogCell';
import { ChangeLogDetailPanel } from './ChangeLogDetailPanel';
import { formatQuotationChangeLogsSummary, parseQuotationChangeLogs } from './formatQuotationChangeLogs';

export type QuotationLogsTabScreenProps = {
  quotationDocumentId: string;
  readOnly: boolean;
};

function changeLogsSnippet(row: DynamicEntityRow): string {
  const summary = formatQuotationChangeLogsSummary(row.change_logs);
  if (summary) return summary;
  return '—';
}

function changeLogsJsonString(row: DynamicEntityRow): string {
  const c = row.change_logs;
  if (c == null) return '{}';
  try {
    return JSON.stringify(c, null, 2);
  } catch {
    return '{}';
  }
}

/** Riwayat perubahan field quotation (label: lama → baru). */
export function QuotationLogsTabScreen({ quotationDocumentId, readOnly }: QuotationLogsTabScreenProps) {
  const { jwt } = useAuth();
  const gridApiRef = useGridApiRef();
  const listQ = useQuotationLogsQuery(jwt, quotationDocumentId);
  const createMut = useCreateQuotationLogMutation(jwt, quotationDocumentId);
  const updateMut = useUpdateQuotationLogMutation(jwt, quotationDocumentId);
  const deleteMut = useDeleteQuotationLogMutation(jwt, quotationDocumentId);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'create' | 'edit'>('create');
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(null);
  const [jsonText, setJsonText] = useState('{}');
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DynamicEntityRow | null>(null);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(() => new Set());

  const rows = listQ.data ?? [];

  const toggleRowExpanded = useCallback((documentId: string) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(documentId)) next.delete(documentId);
      else next.add(documentId);
      return next;
    });
    requestAnimationFrame(() => {
      gridApiRef.current?.resetRowHeights();
    });
  }, [gridApiRef]);

  const openCreate = useCallback(() => {
    setEditorMode('create');
    setEditingDocumentId(null);
    setJsonText('{\n  "field_name": { "old": "", "new": "" }\n}');
    setFormError(null);
    setEditorOpen(true);
  }, []);

  const openEdit = useCallback((row: DynamicEntityRow) => {
    setEditorMode('edit');
    setEditingDocumentId(row.documentId);
    setJsonText(changeLogsJsonString(row));
    setFormError(null);
    setEditorOpen(true);
  }, []);

  async function submitEditor(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    const t = jsonText.trim();
    if (!t) {
      setFormError('JSON tidak boleh kosong.');
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(t) as unknown;
    } catch {
      setFormError('JSON tidak valid.');
      return;
    }
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setFormError('Harus berupa object JSON (bukan array atau null).');
      return;
    }
    try {
      if (editorMode === 'create') {
        await createMut.mutateAsync(parsed);
      } else if (editingDocumentId) {
        await updateMut.mutateAsync({ documentId: editingDocumentId, change_logs: parsed });
      }
      setEditorOpen(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Gagal menyimpan.');
    }
  }

  const columns = useMemo((): GridColDef<DynamicEntityRow>[] => {
    const base: GridColDef<DynamicEntityRow>[] = [
      {
        field: '__expand',
        headerName: '',
        width: 52,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const id = params.row.documentId;
          const expanded = expandedRowIds.has(id);
          const entryCount = parseQuotationChangeLogs(params.row.change_logs).length;
          return (
            <Tooltip title={expanded ? 'Tutup detail' : 'Lihat detail perubahan'}>
              <span>
                <IconButton
                  size="small"
                  aria-expanded={expanded}
                  aria-label={expanded ? 'Tutup detail log' : 'Lihat detail log'}
                  data-testid={`quotation-log-expand-${id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleRowExpanded(id);
                  }}
                  disabled={entryCount === 0 && !expanded}
                >
                  {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          );
        },
      },
      {
        field: 'changes',
        headerName: 'Perubahan',
        flex: 1,
        minWidth: 280,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const expanded = expandedRowIds.has(params.row.documentId);
          if (expanded) {
            return <ChangeLogDetailPanel row={params.row} />;
          }
          return <ChangeLogCell change_logs={params.row.change_logs} compact />;
        },
      },
      {
        field: 'updatedAt',
        headerName: 'Waktu',
        flex: 0.35,
        minWidth: 140,
        valueGetter: (_, row) =>
          row.updatedAt != null ? String(row.updatedAt).slice(0, 19).replace('T', ' ') : '—',
      },
    ];
    if (!readOnly) {
      base.push({
        field: 'actions',
        type: 'actions',
        headerName: '',
        width: 88,
        getActions: (params) => [
          <GridActionsCellItem
            key="e"
            icon={<EditOutlinedIcon fontSize="small" />}
            label="Edit"
            onClick={() => openEdit(params.row)}
          />,
          <GridActionsCellItem
            key="d"
            icon={<DeleteOutlineIcon fontSize="small" />}
            label="Delete"
            onClick={() => setPendingDelete(params.row)}
          />,
        ],
      });
    }
    return base;
  }, [readOnly, openEdit, expandedRowIds, toggleRowExpanded]);

  async function confirmDelete() {
    if (!pendingDelete?.documentId) return;
    try {
      await deleteMut.mutateAsync(pendingDelete.documentId);
      deleteMut.reset();
      setPendingDelete(null);
    } catch {
      /* dialog */
    }
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        Setiap baris menampilkan ringkasan perubahan. Gunakan tombol expand (▸) untuk melihat detail
        lengkap per field (nilai lama → nilai baru). Perubahan dari penyimpanan quotation dicatat otomatis.
      </Typography>
      {listQ.isError && (
        <Alert severity="error">
          {listQ.error instanceof Error ? listQ.error.message : 'Gagal memuat log quotation.'}
        </Alert>
      )}
      {!listQ.isPending && !listQ.isError && rows.length === 0 && (
        <Alert severity="info">
          Belum ada log.{' '}
          {readOnly ? (
            <>
              Perubahan dari penyimpanan ubah quotation tercatat otomatis di server. Tambah manual hanya dari
              halaman ubah.
            </>
          ) : (
            'Tambahkan entri pertama.'
          )}
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        {!readOnly && (
          <Tooltip title="Tambah log">
            <IconButton color="primary" onClick={openCreate} aria-label="Tambah log" size="small">
              <AddIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box sx={{ width: '100%' }} data-testid="quotation-logs-data-grid">
        <DataGrid
          apiRef={gridApiRef}
          rows={rows}
          columns={columns}
          loading={listQ.isPending}
          getRowId={(r) => r.documentId || `missing:${Math.random()}`}
          getRowHeight={(params) => (expandedRowIds.has(String(params.id)) ? 'auto' : 52)}
          getEstimatedRowHeight={() => 160}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: DATA_GRID_DEFAULT_PAGE_SIZE, page: 0 } } }}
          autoHeight
          density="compact"
          sx={{
            '& .MuiDataGrid-columnHeaders': { fontWeight: 600 },
            '& .MuiDataGrid-cell': { alignItems: 'flex-start', py: 0.75 },
          }}
        />
      </Box>

      <Dialog
        open={editorOpen}
        onClose={() => !createMut.isPending && !updateMut.isPending && setEditorOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{editorMode === 'create' ? 'Log perubahan baru' : 'Ubah log'}</DialogTitle>
        <Box component="form" onSubmit={(ev) => void submitEditor(ev)}>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <TextField
                label="Change logs (JSON object)"
                multiline
                minRows={14}
                size="small"
                fullWidth
                value={jsonText}
                onChange={(ev) => setJsonText(ev.target.value)}
                slotProps={{
                  input: {
                    sx: { fontFamily: 'ui-monospace, monospace', fontSize: '0.8125rem' },
                  },
                }}
                helperText='Contoh: {"status":{"old":"draft","new":"sent"}}'
              />
              {formError ? (
                <Typography variant="body2" color="error">
                  {formError}
                </Typography>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button type="button" onClick={() => setEditorOpen(false)}>
              Batal
            </Button>
            <Button type="submit" variant="contained" disabled={createMut.isPending || updateMut.isPending}>
              {createMut.isPending || updateMut.isPending ? 'Menyimpan…' : 'Simpan'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(pendingDelete)} onClose={() => !deleteMut.isPending && setPendingDelete(null)} maxWidth="xs">
        <DialogTitle>Hapus log?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Cuplikan: {pendingDelete ? changeLogsSnippet(pendingDelete) : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {pendingDelete?.documentId}
          </Typography>
          {deleteMut.isError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {deleteMut.error instanceof Error ? deleteMut.error.message : 'Hapus gagal.'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !deleteMut.isPending && setPendingDelete(null)} disabled={deleteMut.isPending}>
            Batal
          </Button>
          <Button color="error" variant="contained" onClick={() => void confirmDelete()} disabled={deleteMut.isPending}>
            {deleteMut.isPending ? 'Menghapus…' : 'Hapus'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
